import React, { useState, useEffect } from 'react';
import { useGlobalProvider } from '../contexts/GlobalProvider';
import { useAuth } from '../contexts/AuthContext';
import { createRelationship } from '../api/createRelationshipService';
import { docsMetaDataService } from '../api/docsMetaDataService';
import { openViewerWithDocument, extractUrnFromDirectoryNode, isFileNode } from '../utilities/viewerOperations';
import { buildFileUrl, buildFolderUrl } from '../utilities/regionUtils';
import { useViewerSupport } from '../hooks/useViewerSupport';

const DirectoryButtonBar = ({ setSelectedLeftUrn, setSelectedRightUrn, openViewerLeft, openViewerRight }) => {
    const {
        accCurrentNode,
        setAccCurrentNode,
        accTopFolder,
        selectedProjectId,
        selectedHubRegion,
        selectedTagId,
        selectedAssetId,
        triggerTreeRefresh
    } = useGlobalProvider();
    
    const { getAccessToken, isAuthenticated, isFetchingToken } = useAuth();
    const { checkSupport, loading: supportLoading } = useViewerSupport();
    const [isCreatingRelationship, setIsCreatingRelationship] = useState(false);
    const [relationshipMessage, setRelationshipMessage] = useState('');
    const [isViewerSupported, setIsViewerSupported] = useState(null);

    // Check viewer support when accCurrentNode changes
    useEffect(() => {
        const checkFileSupport = async () => {
            const nodeObj = typeof accCurrentNode === 'object' ? accCurrentNode : null;
            
            if (!nodeObj || !isFileNode(nodeObj)) {
                setIsViewerSupported(null);
                return;
            }

            // Get the file name from the node
            const fileName = nodeObj.name;
            console.log('Checking support for file:', fileName);
            
            if (!fileName) {
                setIsViewerSupported(false);
                return;
            }

            try {
                const isSupported = await checkSupport(fileName);
                setIsViewerSupported(isSupported);
            } catch (error) {
                console.error('Error checking file viewer support:', error);
                setIsViewerSupported(false);
            }
        };

        checkFileSupport();
    }, [accCurrentNode, checkSupport]);

    const handleRefresh = () => {
        console.log('Refreshing directory tree from API...');
        // Trigger a real refresh of the tree data from the API
        triggerTreeRefresh();
    };

    const handleCreateRelationship = async () => {
        if (!selectedAssetId || !accCurrentNode || !selectedProjectId || selectedProjectId.trim() === '') {
            setRelationshipMessage('Please select both an asset and a file to create a relationship.');
            setTimeout(() => setRelationshipMessage(''), 3000);
            return;
        }

        setIsCreatingRelationship(true);
        setRelationshipMessage('');

        try {
            console.log('[DirectoryButtonBar] === RELATIONSHIP CREATION DEBUG ===');
            console.log('[DirectoryButtonBar] selectedAssetId:', selectedAssetId);
            console.log('[DirectoryButtonBar] accCurrentNode:', accCurrentNode);
            console.log('[DirectoryButtonBar] selectedProjectId:', selectedProjectId);
            console.log('[DirectoryButtonBar] accCurrentNode type:', typeof accCurrentNode);
            
            const fileId = accCurrentNode.id || accCurrentNode;
            console.log('[DirectoryButtonBar] Extracted fileId:', fileId);
            
            console.log('[DirectoryButtonBar] Getting access token...');
            const accessToken = await getAccessToken();
            console.log('[DirectoryButtonBar] Access token obtained:', accessToken ? 'YES' : 'NO');
            
            const relationshipData = {
                assetId: selectedAssetId,
                fileId: fileId,
                relationshipType: 'ATTACHMENT'
            };
            
            console.log('[DirectoryButtonBar] Final relationshipData:', relationshipData);
            console.log('[DirectoryButtonBar] === END DEBUG INFO ===');

            const result = await createRelationship(accessToken, selectedProjectId, relationshipData);
            
            console.log('[DirectoryButtonBar] Relationship created successfully:', result);
            setRelationshipMessage('✅ Relationship created successfully!');
            setTimeout(() => setRelationshipMessage(''), 5000);

        } catch (error) {
            console.error('[DirectoryButtonBar] Error creating relationship:', error);
            setRelationshipMessage(`❌ Error: ${error.message}`);
            setTimeout(() => setRelationshipMessage(''), 5000);
        } finally {
            setIsCreatingRelationship(false);
        }
    };

    const fetchDocumentUrn = async (documentId) => {
        try {
            console.log('Fetching document metadata for ID:', documentId);
            const accessToken = await getAccessToken();
            const documentMetaData = await docsMetaDataService({
                selectedProjectId,
                documentIds: documentId,
                accessToken
            });
            
            console.log('Document metadata response:', documentMetaData);
            
            // Extract the proper URN from the metadata response
            // Based on AssetRelationShipsTable, we need to get the URN from the included data
            if (documentMetaData && documentMetaData.included && documentMetaData.included.length > 0) {
                const versionData = documentMetaData.included.find(item => item.type === 'versions');
                if (versionData && versionData.id) {
                    const properUrn = versionData.id;
                    console.log('Extracted proper URN from metadata:', properUrn);
                    return properUrn;
                }
            }
            
            // Fallback: try to construct the URN from the response data
            if (documentMetaData && documentMetaData.data && documentMetaData.data.id) {
                console.log('Using fallback URN from data.id:', documentMetaData.data.id);
                return documentMetaData.data.id;
            }
            
            throw new Error('Could not extract URN from document metadata');
        } catch (error) {
            console.error('Error fetching document URN:', error);
            throw error;
        }
    };

    const handleOpenInViewer = async () => {
        const nodeObj = typeof accCurrentNode === 'object' ? accCurrentNode : null;
        
        if (!nodeObj || !isFileNode(nodeObj)) {
            console.log('DirectoryButtonBar - Early return - no current node or not a file');
            return;
        }
        
        // For directory nodes, we need to handle URN extraction differently
        // Some nodes have direct URNs, others need API calls
        let urn = extractUrnFromDirectoryNode(nodeObj);
        
        // If no direct URN found, try to fetch it via API
        if (!urn && nodeObj.id) {
            try {
                urn = await fetchDocumentUrn(nodeObj.id);
            } catch (error) {
                console.error('Failed to fetch URN for document:', nodeObj.id, error);
                return;
            }
        }
        
        await openViewerWithDocument(openViewerLeft, setSelectedLeftUrn, urn, 'left');
    };

    const handleOpenInViewerRight = async () => {
        const nodeObj = typeof accCurrentNode === 'object' ? accCurrentNode : null;
        
        if (!nodeObj || !isFileNode(nodeObj)) {
            console.log('DirectoryButtonBar - Early return - no current node or not a file');
            return;
        }
        
        // For directory nodes, we need to handle URN extraction differently
        // Some nodes have direct URNs, others need API calls
        let urn = extractUrnFromDirectoryNode(nodeObj);
        
        // If no direct URN found, try to fetch it via API
        if (!urn && nodeObj.id) {
            try {
                urn = await fetchDocumentUrn(nodeObj.id);
            } catch (error) {
                console.error('Failed to fetch URN for document:', nodeObj.id, error);
                return;
            }
        }
        
        await openViewerWithDocument(openViewerRight, setSelectedRightUrn, urn, 'right');
    };

    const handleGoUp = () => {
        console.log('button 2...');
        // This would need logic to determine parent folder
        // For now, just log the action
    };

    const handleOpenInACC = () => {
        const nodeObj = typeof accCurrentNode === 'object' ? accCurrentNode : null;
        if (!nodeObj) return;

        let url;
        if (isFileNode(nodeObj)) {
            const folderUrn = nodeObj.folderUrn || nodeObj.parent;
            const entityId = nodeObj.versionData?.id || nodeObj.id;

            if (!folderUrn) {
                console.log('No folder URN found for ACC link');
                return;
            }

            url = buildFileUrl(
                selectedHubRegion,
                selectedProjectId,
                folderUrn,
                entityId,
            );
        } else {
            const folderUrn = nodeObj.id;
            url = buildFolderUrl(selectedHubRegion, selectedProjectId, folderUrn);
        }

        window.open(url, '_blank');
    };

    // Check if the selected node is a file (not a folder)
    const isFileSelected = () => {
        if (!accCurrentNode) return false;
        
        const nodeObj = typeof accCurrentNode === 'object' ? accCurrentNode : null;
        
        if (nodeObj) {
            return isFileNode(nodeObj);
        }
        
        // If accCurrentNode is a string, assume it's a file ID
        return typeof accCurrentNode === 'string' && accCurrentNode.length > 0;
    };

    const isDisabled = !selectedProjectId || selectedProjectId.trim() === '' || !accTopFolder;
    const isRelationshipDisabled = !selectedAssetId || !accCurrentNode || !selectedProjectId || selectedProjectId.trim() === '' || isCreatingRelationship || !isFileSelected();
    
    // Enhanced logic that includes viewer support check
    const isViewerDisabled = !isAuthenticated || 
                             isFetchingToken || 
                             !accCurrentNode || 
                             !isFileSelected() || 
                             supportLoading || 
                             (isViewerSupported !== null && isViewerSupported === false);
    
    // Debug logging
    console.log('DirectoryButtonBar Debug:', {
        isAuthenticated,
        isFetchingToken,
        accCurrentNode,
        isFileSelected: isFileSelected(),
        supportLoading,
        isViewerSupported,
        isViewerDisabled
    });
    
    const isOpenInACCDisabled = !accCurrentNode;
    
    // Generate appropriate tooltip messages for viewer buttons
    const getViewerTooltip = () => {
        if (!isAuthenticated) return "Please log in to use the viewer";
        if (isFetchingToken) return "Loading authentication...";
        if (!accCurrentNode) return "Please select a file from the document tree";
        if (!isFileSelected()) return "Please select a file (not a folder) to open in viewer";
        if (supportLoading) return "Checking if file is supported by viewer...";
        if (isViewerSupported === false) return "This file type is not supported by the viewer";
        return "Open selected file in viewer";
    };

    return (
        <div className="mb-3">
            <div className="btn-toolbar" role="toolbar" aria-label="Directory navigation toolbar">
                <div className="btn-group" role="group" aria-label="Action buttons">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleRefresh}
                        disabled={isDisabled}
                        title="Refresh directory tree"
                    >
                        <i className="bi bi-arrow-clockwise"></i> Refresh
                    </button>
                </div>
                <div className="btn-group me-2" role="group" aria-label="Viewer buttons">
                    <button
                        type="button"
                        className={`btn btn-sm ${isViewerDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                        onClick={handleOpenInViewer}
                        disabled={isViewerDisabled}
                        title={isViewerDisabled ? getViewerTooltip() : "Open selected file in left viewer"}
                    >
                        <i className="bi bi-eye"></i> View Left
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${isViewerDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                        onClick={handleOpenInViewerRight}
                        disabled={isViewerDisabled}
                        title={isViewerDisabled ? getViewerTooltip() : "Open selected file in right viewer"}
                    >
                        <i className="bi bi-eye"></i> View Right
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${isOpenInACCDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                        onClick={handleOpenInACC}
                        disabled={isOpenInACCDisabled}
                        title={isOpenInACCDisabled ?
                            "Please select a file or folder to open in ACC" :
                            "Open selected item in ACC"
                        }
                    >
                        <i className="bi bi-box-arrow-up-right"></i> Open in ACC
                    </button>
                </div>
                <div className="btn-group me-2" role="group" aria-label="Navigation buttons">
                    <button
                        type="button"
                        className={`btn btn-sm ${isRelationshipDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                        onClick={handleCreateRelationship}
                        disabled={isRelationshipDisabled}
                        title={isRelationshipDisabled ?
                            (!selectedAssetId ? "Select an asset first" :
                             !accCurrentNode ? "Select a file from the document tree" :
                             !isFileSelected() ? "Please select a file (not a folder) from the document tree" :
                             "Select both an asset and a file to create a relationship") :
                            "Create relationship between selected asset and file"
                        }
                    >
                        {isCreatingRelationship ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-link-45deg"></i> Link Asset
                            </>
                        )}
                    </button>

                </div>

            </div>
            {(accCurrentNode || selectedAssetId) && (
                <div className="text-muted small">
                    {accCurrentNode && (
                        <div>
                            {(() => {
                                const nodeObj = typeof accCurrentNode === 'object' ? accCurrentNode : null;
                                if (nodeObj && (nodeObj.isVersion || nodeObj.type === 'version')) {
                                    // Show version information
                                    return `📋 Selected Version: ${nodeObj.name} (${nodeObj.parent ? 'of ' + nodeObj.parent : 'version'})`;
                                } else {
                                    // Show regular file information
                                    return `📁 Selected File: ${nodeObj ? nodeObj.name || nodeObj.id : accCurrentNode}`;
                                }
                            })()}
                        </div>
                    )}
                    {selectedAssetId && (
                        <div>
                            🏗️ Selected Asset: {selectedTagId}
                        </div>
                    )}
                </div>
            )}
            {relationshipMessage && (
                <div className={`alert ${relationshipMessage.includes('✅') ? 'alert-success' : 'alert-danger'} alert-sm mt-2 mb-0`} role="alert">
                    {relationshipMessage}
                </div>
            )}
        </div>
    );
};

export default DirectoryButtonBar;